import {
  basename,
  type DiscoveredFile,
  mergeTurnLines,
  type ParseBatch,
  type SideEvent,
  type TurnEvent,
} from "@tktuner/core";
import type Database from "better-sqlite3";

export interface FileState {
  path: string;
  mtimeMs: number;
  size: number;
  byteOffset: number;
  kind: string;
}

export interface TurnFilter {
  sinceTs?: number;
  projectLike?: string;
}

interface TurnRow {
  uuid: string;
  message_id: string | null;
  request_id: string | null;
  session_id: string;
  agent_id: string | null;
  parent_session_id: string | null;
  is_sidechain: number;
  ts: number;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  cache_5m_tokens: number;
  cache_1h_tokens: number;
  tools: string;
  num_tool_uses: number;
  thinking_chars: number;
  text_chars: number;
  stop_reason: string | null;
  cwd: string | null;
  slug: string | null;
  git_branch: string | null;
  version: string | null;
  speed: string | null;
  inference_geo: string | null;
  file_path: string;
}

export class Store {
  private readonly stmtGetFile;
  private readonly stmtUpsertFile;
  private readonly stmtInsertTurn;
  private readonly stmtInsertEvent;
  private readonly txIngest;

  constructor(private readonly db: Database.Database) {
    this.stmtGetFile = db.prepare(
      `SELECT path, mtime_ms AS mtimeMs, size, byte_offset AS byteOffset, kind
       FROM files WHERE path = ?`,
    );
    this.stmtUpsertFile = db.prepare(
      `INSERT INTO files (path, mtime_ms, size, byte_offset, kind, session_id, agent_id, parent_session_id, last_scanned_at)
       VALUES (@path, @mtimeMs, @size, @byteOffset, @kind, @sessionId, @agentId, @parentSessionId, @scannedAt)
       ON CONFLICT(path) DO UPDATE SET
         mtime_ms = excluded.mtime_ms,
         size = excluded.size,
         byte_offset = excluded.byte_offset,
         last_scanned_at = excluded.last_scanned_at`,
    );
    this.stmtInsertTurn = db.prepare(
      `INSERT OR IGNORE INTO turns (
         uuid, message_id, request_id, session_id, agent_id, parent_session_id, is_sidechain, ts, model,
         input_tokens, output_tokens, cache_creation_tokens, cache_read_tokens,
         cache_5m_tokens, cache_1h_tokens, tools, num_tool_uses, thinking_chars,
         text_chars, stop_reason, cwd, slug, git_branch, version, speed, inference_geo, file_path
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    );
    this.stmtInsertEvent = db.prepare(
      `INSERT INTO events (session_id, ts, kind, data, file_path) VALUES (?, ?, ?, ?, ?)`,
    );
    this.txIngest = db.transaction((file: DiscoveredFile, batch: ParseBatch) => {
      let newTurns = 0;
      for (const t of batch.turns) {
        const info = this.stmtInsertTurn.run(
          t.uuid,
          t.messageId ?? null,
          t.requestId ?? null,
          t.sessionId,
          t.agentId ?? null,
          t.parentSessionId ?? null,
          t.isSidechain ? 1 : 0,
          t.timestamp,
          t.model,
          t.usage.input,
          t.usage.output,
          t.usage.cacheWrite,
          t.usage.cacheRead,
          t.usage.cacheWrite5m,
          t.usage.cacheWrite1h,
          JSON.stringify(t.tools),
          t.numToolUses,
          t.thinkingChars,
          t.textChars,
          t.stopReason ?? null,
          t.projectPath || null,
          t.slug ?? null,
          t.gitBranch ?? null,
          t.cliVersion ?? null,
          t.speed ?? null,
          t.inferenceGeo ?? null,
          t.filePath,
        );
        newTurns += info.changes;
      }
      for (const e of batch.events) {
        this.stmtInsertEvent.run(
          e.sessionId,
          e.timestamp ?? null,
          e.kind,
          JSON.stringify(e.data),
          e.filePath,
        );
      }
      this.stmtUpsertFile.run({
        path: file.path,
        mtimeMs: file.mtimeMs,
        size: file.sizeBytes,
        byteOffset: batch.bytesConsumed,
        kind: file.kind,
        sessionId: file.sessionId ?? null,
        agentId: file.agentId ?? null,
        parentSessionId: file.parentSessionId ?? null,
        scannedAt: Date.now(),
      });
      return { newTurns, newEvents: batch.events.length };
    });
  }

  getFileState(path: string): FileState | undefined {
    return this.stmtGetFile.get(path) as FileState | undefined;
  }

  /** Remove everything ingested from one file so it can be reparsed cleanly. */
  deleteFileData(path: string): void {
    this.db.prepare(`DELETE FROM turns WHERE file_path = ?`).run(path);
    this.db.prepare(`DELETE FROM events WHERE file_path = ?`).run(path);
    this.db.prepare(`DELETE FROM files WHERE path = ?`).run(path);
  }

  ingestBatch(file: DiscoveredFile, batch: ParseBatch): { newTurns: number; newEvents: number } {
    return this.txIngest(file, batch) as { newTurns: number; newEvents: number };
  }

  /** Merge unknown line-type counts into the persistent parser-evolution log. */
  bumpUnknownTypes(counts: Record<string, number>): void {
    if (Object.keys(counts).length === 0) return;
    const merged = { ...this.getUnknownTypes() };
    for (const [k, v] of Object.entries(counts)) merged[k] = (merged[k] ?? 0) + v;
    this.setMeta("unknown_line_types", JSON.stringify(merged));
  }

  getUnknownTypes(): Record<string, number> {
    const raw = this.getMeta("unknown_line_types");
    if (!raw) return {};
    try {
      return JSON.parse(raw) as Record<string, number>;
    } catch {
      return {};
    }
  }

  getMeta(key: string): string | undefined {
    const row = this.db.prepare(`SELECT value FROM meta WHERE key = ?`).get(key) as
      | { value: string }
      | undefined;
    return row?.value;
  }

  setMeta(key: string, value: string): void {
    this.db
      .prepare(
        `INSERT INTO meta (key, value) VALUES (?, ?)
         ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
      )
      .run(key, value);
  }

  /** Real (merged) turn count — content-block lines collapse by message_id. */
  countTurns(): number {
    const row = this.db
      .prepare(`SELECT COUNT(DISTINCT COALESCE(message_id, uuid)) AS n FROM turns`)
      .get() as { n: number };
    return row.n;
  }

  /**
   * Loads billable turns. Raw rows are one-per-content-block (Claude Code
   * splits an API response across JSONL lines, repeating the same usage), so
   * rows sharing a message_id are merged here into a single TurnEvent.
   *
   * The project filter matches the project NAME (basename of cwd), case-
   * insensitive — the same semantics as the dashboard, which only ever sees
   * hashed ids + names. Full-path matching would also catch unrelated
   * directories that merely contain the string.
   */
  loadTurns(filter: TurnFilter = {}): TurnEvent[] {
    const clauses: string[] = [];
    const params: unknown[] = [];
    if (filter.sinceTs !== undefined) {
      clauses.push(`ts >= ?`);
      params.push(filter.sinceTs);
    }
    const where = clauses.length ? `WHERE ${clauses.join(" AND ")}` : "";
    let rows = this.db
      .prepare(`SELECT * FROM turns ${where} ORDER BY ts ASC, uuid ASC`)
      .all(...params) as TurnRow[];
    if (filter.projectLike) {
      const needle = filter.projectLike.toLowerCase();
      rows = rows.filter((r) =>
        basename(r.cwd ?? "")
          .toLowerCase()
          .includes(needle),
      );
    }
    return mergeTurnLines(rows.map(rowToTurn));
  }

  /** Distinct turnKeys touched by raw lines after `rowid` + the new watermark. */
  newTurnKeysSince(rowid: number): { keys: string[]; maxRowid: number } {
    const rows = this.db
      .prepare(`SELECT rowid AS rid, COALESCE(message_id, uuid) AS k FROM turns WHERE rowid > ?`)
      .all(rowid) as { rid: number; k: string }[];
    const keys = new Set<string>();
    let maxRowid = rowid;
    for (const r of rows) {
      keys.add(r.k);
      if (r.rid > maxRowid) maxRowid = r.rid;
    }
    return { keys: [...keys], maxRowid };
  }

  /**
   * Loads and merges the ENTIRE line group of each turnKey (late-arriving
   * content blocks re-push the whole merged turn — the server upsert
   * overwrites). Each merged turn carries its group's max rowid so the sync
   * watermark can advance per confirmed batch.
   */
  loadTurnGroups(keys: string[]): { turn: TurnEvent; maxRowid: number }[] {
    if (keys.length === 0) return [];
    const lines: TurnEvent[] = [];
    const maxRowidByKey = new Map<string, number>();
    for (let i = 0; i < keys.length; i += 500) {
      const chunk = keys.slice(i, i + 500);
      const placeholders = chunk.map(() => "?").join(",");
      const rows = this.db
        .prepare(
          `SELECT rowid AS rid, * FROM turns
            WHERE COALESCE(message_id, uuid) IN (${placeholders})
            ORDER BY ts ASC, uuid ASC`,
        )
        .all(...chunk) as (TurnRow & { rid: number })[];
      for (const r of rows) {
        const key = r.message_id ?? r.uuid;
        const prev = maxRowidByKey.get(key) ?? 0;
        if (r.rid > prev) maxRowidByKey.set(key, r.rid);
        lines.push(rowToTurn(r));
      }
    }
    return mergeTurnLines(lines)
      .map((turn) => ({
        turn,
        maxRowid: maxRowidByKey.get(turn.messageId ?? turn.uuid) ?? 0,
      }))
      .sort((a, b) => a.maxRowid - b.maxRowid);
  }

  /** Raw events after `id`, oldest first (insert-only; id is the watermark). */
  eventsSince(id: number): { id: number; event: SideEvent }[] {
    const rows = this.db
      .prepare(
        `SELECT id, session_id, ts, kind, data, file_path FROM events WHERE id > ? ORDER BY id ASC`,
      )
      .all(id) as {
      id: number;
      session_id: string;
      ts: number | null;
      kind: string;
      data: string;
      file_path: string;
    }[];
    return rows.map((r) => {
      let data: Record<string, unknown> = {};
      try {
        data = JSON.parse(r.data) as Record<string, unknown>;
      } catch {
        // keep {}
      }
      return {
        id: r.id,
        event: {
          kind: r.kind as SideEvent["kind"],
          sessionId: r.session_id,
          timestamp: r.ts ?? undefined,
          filePath: r.file_path,
          data,
        },
      };
    });
  }

  loadEvents(): SideEvent[] {
    const rows = this.db
      .prepare(`SELECT session_id, ts, kind, data, file_path FROM events ORDER BY ts ASC`)
      .all() as {
      session_id: string;
      ts: number | null;
      kind: string;
      data: string;
      file_path: string;
    }[];
    return rows.map((r) => {
      let data: Record<string, unknown> = {};
      try {
        data = JSON.parse(r.data) as Record<string, unknown>;
      } catch {
        // keep {}
      }
      return {
        kind: r.kind as SideEvent["kind"],
        sessionId: r.session_id,
        timestamp: r.ts ?? undefined,
        filePath: r.file_path,
        data,
      };
    });
  }
}

function rowToTurn(r: TurnRow): TurnEvent {
  let tools: string[] = [];
  try {
    const parsed = JSON.parse(r.tools) as unknown;
    if (Array.isArray(parsed)) tools = parsed.filter((x): x is string => typeof x === "string");
  } catch {
    // keep []
  }
  return {
    agent: "claude-code",
    uuid: r.uuid,
    messageId: r.message_id ?? undefined,
    requestId: r.request_id ?? undefined,
    sessionId: r.session_id,
    parentSessionId: r.parent_session_id ?? undefined,
    agentId: r.agent_id ?? undefined,
    isSidechain: r.is_sidechain === 1,
    projectPath: r.cwd ?? "",
    slug: r.slug ?? undefined,
    timestamp: r.ts,
    model: r.model,
    usage: {
      input: r.input_tokens,
      output: r.output_tokens,
      cacheRead: r.cache_read_tokens,
      cacheWrite: r.cache_creation_tokens,
      cacheWrite5m: r.cache_5m_tokens,
      cacheWrite1h: r.cache_1h_tokens,
    },
    tools,
    numToolUses: r.num_tool_uses,
    thinkingChars: r.thinking_chars,
    textChars: r.text_chars,
    stopReason: r.stop_reason ?? undefined,
    gitBranch: r.git_branch ?? undefined,
    cliVersion: r.version ?? undefined,
    speed: r.speed ?? undefined,
    inferenceGeo: r.inference_geo ?? undefined,
    filePath: r.file_path,
  };
}
